import { useUser } from "@clerk/clerk-react";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { trpc } from "~/lib/trpc";

export type StudioStatus = "loading" | "started" | "failed" | "closed";

export type StudioManager = {
  status: StudioStatus;
  open: (url: string, highlight?: string) => Window | null;
  close: () => void;
  postTokenToWindow: (window: Window, targetOrigin: string) => Promise<void>;
  focusStudio: () => void;
  studioUrl: string | null;
};

export function useStudioManager(): StudioManager {
  const { user } = useUser();

  const [studioWindow, setStudioWindow] = useState<Window | null>(null);
  const [studioUrl, setStudioUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<StudioStatus>("closed");

  // tRPC mutation for generating auth tokens
  const generateTokenMutation = useMutation(
    trpc.studioAuth.generateToken.mutationOptions(),
  );

  // Function to open a URL in a new tab and maintain a handle to it
  const openEditor = useCallback(
    (url: string, highlight?: string) => {
      // Close any previously opened window
      if (studioWindow && !studioWindow.closed) {
        studioWindow.close();
      }

      // Open new window
      const newUrl = new URL(url);
      newUrl.searchParams.set("mode", "studio");

      if (highlight) {
        newUrl.searchParams.set("highlight", highlight);
      }

      const newWindow = window.open(newUrl, "_blank");

      setStudioWindow(newWindow);
      setStudioUrl(newUrl.toString());
      setStatus("loading");

      return newWindow;
    },
    [studioWindow],
  );

  // Listen for REQUEST_AUTH messages from opened windows
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Only handle messages from windows we opened
      if (event.source !== studioWindow) {
        console.log("Not from our window");
        return;
      }

      // Check if it's a REQUEST_AUTH message
      if (event.data?.type === "REQUEST_AUTH_TOKEN") {
        try {
          console.log(
            "Received REQUEST_AUTH_TOKEN from editor window",
            event.origin,
          );

          // Post AUTH_TOKEN message back to the child window
          if (studioWindow && !studioWindow.closed) {
            postTokenToWindow(studioWindow, event.origin);
            setStatus("started");
            console.debug("Sent AUTH_TOKEN to editor window", event.origin);
          }
        } catch (error) {
          console.error("Failed to generate auth token:", error);
          // Optionally send an error message back to the editor window
          if (studioWindow && !studioWindow.closed) {
            studioWindow.postMessage(
              { type: "AUTH_ERROR", error: "Failed to generate token" },
              event.origin,
            );
          }
          setStatus("failed");
        }
      } else {
        console.log("Not a REQUEST_AUTH message");
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [studioWindow]);

  const postTokenToWindow = useCallback(
    async (window: Window, targetOrigin: string) => {
      // Call tRPC to generate token
      try {
        const result = await generateTokenMutation.mutateAsync({});

        if (window && !window.closed) {
          window.postMessage(
            { type: "AUTH_TOKEN", token: result.token, userId: user?.id },
            targetOrigin,
          );
        }
      } catch (error) {
        console.error("Failed to generate token:", error);
      }
    },
    [generateTokenMutation, user?.id],
  );

  // Cleanup function to close the opened window
  const close = useCallback(() => {
    if (studioWindow && !studioWindow.closed) {
      studioWindow.close();
      setStudioWindow(null);
      setStudioUrl(null);
    }
  }, [studioWindow]);

  useEffect(() => {
    if (!user) {
      console.log("closing studio");
      close();
    }
  }, [user, close]);

  function focusStudio() {
    if (studioWindow && !studioWindow.closed) {
      studioWindow.focus();
    }
  }

  useEffect(() => {
    if (status === "loading" || status === "started") {
      const interval = setInterval(() => {
        if (!studioWindow || studioWindow?.closed) {
          if (status === "loading") {
            setStatus(studioWindow?.closed ? "closed" : "failed");
          } else if (status === "started") {
            setStatus("closed");
          }
        }
      }, 500);

      return () => clearInterval(interval);
    }
  }, [studioWindow, status]);

  return {
    status,
    open: openEditor,
    close,
    postTokenToWindow,
    focusStudio,
    studioUrl,
  };
}
